import React from 'react';
import { useFavorites } from '../context/FavoritesContext';

const HomepageHotelCard = ({
    id,
    image,
    title = "Hotel Name",
    location = "Location",
    rating = 4.4,
    price = 800,
    originalPrice = null,
    discountPercent = 0,
    isSoldOut = false,
    onCardClick = () => { },
    onBookNow = () => { },
}) => {
    const { favoriteIds, toggleFavorite } = useFavorites();
    const isLiked = favoriteIds.includes(id);

    const handleFavorite = (e) => {
        e.stopPropagation();
        if (!id) return;
        toggleFavorite(id);
    };

    const handleBookNow = (e) => {
        e.stopPropagation();
        onBookNow();
    };

    return (
        <div
            className="bg-white rounded-2xl border border-gray-100/80 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-1 cursor-pointer"
            onClick={onCardClick}
        >
            {/* Image Section */}
            <div className="relative">
                <img
                    src={image}
                    alt={title}
                    className="w-full h-48 object-cover"
                />

                {/* Badges */}
                <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                    <div className="flex flex-col gap-1">
                        {discountPercent > 0 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-red-500 text-white shadow-sm uppercase tracking-wider">
                                {discountPercent}% OFF
                            </span>
                        )}
                        {isSoldOut && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-gray-800 text-white shadow-sm uppercase tracking-wider">
                                SOLD OUT
                            </span>
                        )}
                    </div>

                    <button
                        onClick={handleFavorite}
                        className="p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white transition-all duration-200 border-0"
                    >
                        <svg
                            className={`w-4 h-4 transition-colors duration-200 ${isLiked ? 'text-red-500 fill-current' : 'text-gray-600'}`}
                            fill={isLiked ? "currentColor" : "none"}
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                    </button>
                </div>

                {/* Rating Badge */}
                <div className="absolute bottom-3 left-3">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-xl text-white text-xs font-bold bg-emerald-600/90 backdrop-blur-sm shadow-sm">
                        <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        {Number(rating).toFixed(1)}
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-4 text-start">
                <div className="mb-3">
                    <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-2 capitalize" style={{ lineHeight: '1.4' }}>
                        {title}
                    </h3>
                    <div className="flex items-center text-xs text-gray-500 font-semibold">
                        <svg className="w-3.5 h-3.5 text-gray-400 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <span className="truncate">{location}</span>
                    </div>
                </div>

                {/* Pricing */}
                <div className="flex items-center justify-between mb-3.5">
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-lg font-extrabold text-green-600">₹{price.toLocaleString()}</span>
                        {originalPrice && originalPrice > price && (
                            <span className="text-xs text-gray-400 line-through">₹{originalPrice.toLocaleString()}</span>
                        )}
                    </div>
                    <span className="text-[10px] uppercase font-bold text-gray-400">per night</span>
                </div>

                {/* Book Now Button */}
                <button
                    onClick={handleBookNow}
                    disabled={isSoldOut}
                    className={`w-full py-2.5 px-4 text-xs font-bold text-white rounded-xl transition-all duration-300 shadow-sm border-0 ${
                        isSoldOut 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 hover:shadow-md'
                    }`}
                >
                    {isSoldOut ? 'Sold Out' : 'Book Now'}
                </button>
            </div>
        </div>
    );
};

export default HomepageHotelCard;